// What a cable is called outside Romania.
//
// Every family here is sold on the home market under its Romanian designation.
// An export order has to print the harmonised one instead — the customer's
// paperwork, customs and their own catalogue all use it, and a drum labelled
// "MYYM 3x2.5" is not identifiable to anyone outside the country.
//
// Only the NAME changes. Diameters, the product standard, the performance
// standard, the notified body, the DoP — every one of those is the same
// physical cable and the same certificate, so nothing else may be substituted.
//
// The mapping is stored on the standard (`internationalName`) rather than
// derived at read time: the rule below is MYYM's, and the next family to need
// one will not follow it. What the rule does is SEED the field and offer a
// suggestion in the wizard; a standard whose field is set always wins.

/**
 * MYYM's split, which is the only rule we have.
 *
 * H05VV-F up to and including 4 mm², A05VV-F from 6 mm² up, whatever the
 * conductor count. The harmonised codes differ by voltage class — H05 is
 * 300/500 V, and the heavier sections are the 300/300 V "A" series in this
 * catalogue — so the boundary is the section, not the family.
 *
 * Measured against the live data (2026-09-21): 52 MYYM standards, 38 at or
 * below 4 mm² and 14 at 6 mm² and above. No standard sits between 4 and 6.
 */
export const MYYM_SECTION_BOUNDARY = 4;

export function myymInternationalName(section) {
  const s = Number(section);
  if (!Number.isFinite(s) || s <= 0) return null;
  return s <= MYYM_SECTION_BOUNDARY ? 'H05VV-F' : 'A05VV-F';
}

/** The families that have a rule. Everything else is entered by hand. */
const RULES = {
  MYYM: myymInternationalName
};

/**
 * What to suggest for a family and section, or null when there is no rule.
 *
 * A suggestion, not a verdict: the wizard pre-fills it and the user may type
 * anything else. Returning null for an unknown family is the point — inventing
 * a plausible code for a cable nobody has mapped is worse than leaving it blank,
 * because a wrong designation on export paperwork is a rejected shipment.
 */
export function suggestInternationalName(name, section) {
  const rule = RULES[String(name || '').trim().toUpperCase()];
  return rule ? rule(section) : null;
}

/**
 * The name to PRINT and DISPLAY for a product.
 *
 * @param market 'export' uses the international name when there is one; anything
 *        else, including undefined, keeps the domestic name. Internal is the
 *        default everywhere because internal is ~90% of orders.
 */
export function displayName(standardLike, market) {
  const domestic = standardLike?.name ?? null;
  if (market !== 'export') return domestic;
  const intl =
    standardLike?.internationalName ||
    suggestInternationalName(domestic, standardLike?.section);
  return intl || domestic;
}

export default displayName;
